import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { defineMessages, useIntl } from 'react-intl';

import BreadcrumbStepPropType from '../definitions/BreadcrumbStepPropType';
import Colours from '../definitions/Colours';
import MessagePropType from '../definitions/MessagePropType';
import ElementAttributes from '../definitions/ElementAttributes';
import RemoveDefaultButtonStyles from '../styles/RemoveDefaultButtonStyles';
import MenuIcon from '../graphics/MenuIcon';
import ModalContainer from './ModalContainer';
import ProgressIndicator from './ProgressIndicator';
import Portal from './Portal';


function ProgressIndicatorModal({
    steps,
    onBreadcrumbClick,
    helpMessage,
    className,
    children,
    topRightElement,
}) {
    const intl = useIntl();
    const [ isModalOpen, setIsModalOpen ] = useState(false);

    function handleBreadcrumbClickWithinModal(step) {
        if (onBreadcrumbClick) {
            setIsModalOpen(false);
            onBreadcrumbClick(step);
        }
    }

    return (
        <div className={className}>
            {/* Portal is used to render a hamburger in the site chrome to open the breadcrumbs menu on mobile */}
            <Portal id={ElementAttributes.HEADER_PORTAL_ID_LEFT}>
                <HamburgerButton
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    data-name="cta-click-open-breadcrumb-modal"
                    className="rh-icon-s rh-text-align-left"
                    aria-label={intl.formatMessage(MESSAGES.HAMBURGER_LABEL)}
                >
                    <MenuIcon
                        className="menu-icon rh-icon-s"
                    />
                </HamburgerButton>
            </Portal>

            <ModalContainer
                isOpen={isModalOpen}
                showBranding={false}
                onClose={() => setIsModalOpen(false)}
                topRightElement={topRightElement}
                style={{
                    content: {
                        backgroundColor: Colours.COCONUT_DARK,
                    },
                }}
            >
                <ProgressIndicator
                    steps={steps}
                    onBreadcrumbClick={onBreadcrumbClick ? handleBreadcrumbClickWithinModal : undefined}
                    helpMessage={helpMessage}
                >
                    {children}
                </ProgressIndicator>
            </ModalContainer>
        </div>
    );
}

ProgressIndicatorModal.propTypes = {
    steps: PropTypes.arrayOf(
        BreadcrumbStepPropType,
    ).isRequired,
    onBreadcrumbClick: PropTypes.func,
    helpMessage: MessagePropType,
    className: PropTypes.string,
    children: PropTypes.node,
    topRightElement: PropTypes.node,
};

// This requires its own styled component because its
//  rendered in a Portal
const HamburgerButton = styled.button`
    ${RemoveDefaultButtonStyles};
    cursor: pointer;

    > .menu-icon {
        transition:
            stroke 300ms,
            fill 300ms;
    }
`;

const MESSAGES = defineMessages({
    HAMBURGER_LABEL: {
        id: 'base-ui.ProgressIndicatorModal.hamburgerLabel',
        defaultMessage: 'Open breadcrumb menu',
    },
});

export default observer(ProgressIndicatorModal);
